/**
 * ConnectionStateService - Centralized WebSocket connection state management
 * 
 * This service provides a single source of truth for WebSocket connection state
 * across the entire application. It works with the SharedWorker to ensure
 * consistent state reporting and proper connection lifecycle management.
 * 
 * Features:
 * - Single source of truth for connection state
 * - Real-time connection status updates
 * - Connection health monitoring
 * - Owner and version tracking
 * - Comprehensive connection diagnostics
 * 
 * @class ConnectionStateService
 */
import { Injectable, OnDestroy } from '@angular/core';
import { BehaviorSubject, Observable, Subject, interval } from 'rxjs';
import { distinctUntilChanged, filter, map, takeUntil } from 'rxjs/operators';

import { WebSocketConnectionStatus } from '../../conversation/models/message.model';

/**
 * Connection health status
 */
export interface ConnectionHealth {
  healthy: boolean;
  lastPingTime: number;
  lastPongTime: number;
  lastMessageTime: number;
  lastSendTime: number;
  latency: number;
  messagesReceived: number;
  messagesSent: number;
}

/**
 * Complete connection information
 */
export interface ConnectionInfo {
  state: WebSocketConnectionStatus;
  userId: number | null;
  version: number | null;
  owner: string | null;
  isOwner: boolean;
  health: ConnectionHealth | null;
  lastStateChange: number;
  connectionId: string | null;
  errorDetails: string | null;
}

/**
 * Connection statistics
 */
export interface ConnectionStats {
  uptime: number;
  activeConnections: number;
  activeUsers: number;
  totalConnections: number;
  messagesSent: number;
  messagesReceived: number;
  reconnectAttempts: number;
  errors: number;
  queuedMessages: number;
  processingConnections: number;
  connectionLocks: number;
  healthyConnections: number;
  racePrevented: number;
  staleConnectionsCleaned: number;
}

@Injectable({
  providedIn: 'root'
})
export class ConnectionStateService implements OnDestroy {
  private readonly destroy$ = new Subject<void>();
  
  // Core connection state
  private readonly connectionInfo$ = new BehaviorSubject<ConnectionInfo>({
    state: WebSocketConnectionStatus.DISCONNECTED,
    userId: null,
    version: null,
    owner: null,
    isOwner: false,
    health: null,
    lastStateChange: Date.now(),
    connectionId: null,
    errorDetails: null
  });

  // Connection statistics
  private readonly stats$ = new BehaviorSubject<ConnectionStats | null>(null);

  // State change events
  private readonly stateChange$ = new Subject<{
    from: WebSocketConnectionStatus;
    to: WebSocketConnectionStatus;
    timestamp: number;
    details?: any;
  }>();

  // Connection events
  private readonly connectionEvents$ = new Subject<{
    type: string;
    data: any;
    timestamp: number;
  }>();

  // Configuration
  private readonly config = {
    stateUpdateInterval: 5000, // 5 seconds
    statsUpdateInterval: 10000, // 10 seconds
    healthCheckThreshold: 60000 // 1 minute
  };

  constructor() {
    this.initializeStateMonitoring();
    // console.log('🔗 ConnectionStateService initialized');
  }

  /**
   * Gets the current connection information
   */
  getConnectionInfo(): ConnectionInfo {
    return this.connectionInfo$.getValue();
  }

  /**
   * Observable stream of connection information
   */
  getConnectionInfo$(): Observable<ConnectionInfo> {
    return this.connectionInfo$.asObservable();
  }

  /**
   * Observable stream of connection state only
   */
  getConnectionState$(): Observable<WebSocketConnectionStatus> {
    return this.connectionInfo$.pipe(
      map(info => info.state),
      distinctUntilChanged()
    );
  }

  /**
   * Observable stream of connection health
   */
  getConnectionHealth$(): Observable<ConnectionHealth | null> {
    return this.connectionInfo$.pipe(
      map(info => info.health),
      distinctUntilChanged((prev, curr) => 
        JSON.stringify(prev) === JSON.stringify(curr)
      )
    );
  }

  /**
   * Observable stream of connection statistics
   */
  getStats$(): Observable<ConnectionStats | null> {
    return this.stats$.asObservable();
  }

  /**
   * Observable stream of state change events
   */
  getStateChanges$(): Observable<{
    from: WebSocketConnectionStatus;
    to: WebSocketConnectionStatus;
    timestamp: number;
    details?: any;
  }> {
    return this.stateChange$.asObservable();
  }

  /**
   * Observable stream of connection events
   */
  getConnectionEvents$(): Observable<{
    type: string;
    data: any;
    timestamp: number;
  }> {
    return this.connectionEvents$.asObservable();
  }

  /**
   * Checks if currently connected
   */
  isConnected(): boolean {
    return this.connectionInfo$.getValue().state === WebSocketConnectionStatus.CONNECTED;
  }

  /**
   * Checks if currently connecting
   */
  isConnecting(): boolean {
    const state = this.connectionInfo$.getValue().state;
    return state === WebSocketConnectionStatus.CONNECTING || state === WebSocketConnectionStatus.RECONNECTING;
  }

  /**
   * Checks if connection is healthy
   */
  isHealthy(): boolean {
    const info = this.connectionInfo$.getValue();
    if (!info.health || info.state !== WebSocketConnectionStatus.CONNECTED) {
      return false;
    }

    const now = Date.now();
    const timeSinceLastPong = now - info.health.lastPongTime;
    
    return info.health.healthy && timeSinceLastPong < this.config.healthCheckThreshold;
  }

  /**
   * Checks if current connection is the owner
   */
  isOwner(): boolean {
    return this.connectionInfo$.getValue().isOwner;
  }

  /**
   * Gets current connection version
   */
  getConnectionVersion(): number | null {
    return this.connectionInfo$.getValue().version;
  }

  /**
   * Gets connection latency
   */
  getLatency(): number {
    return this.connectionInfo$.getValue().health?.latency || 0;
  }

  /**
   * Updates connection state
   */
  updateConnectionState(
    state: WebSocketConnectionStatus,
    details?: {
      userId?: number;
      version?: number;
      owner?: string;
      isOwner?: boolean;
      health?: ConnectionHealth;
      connectionId?: string;
      errorDetails?: string;
    }
  ): void {
    const currentInfo = this.connectionInfo$.getValue();
    const previousState = currentInfo.state;

    const newInfo: ConnectionInfo = {
      ...currentInfo,
      state,
      lastStateChange: Date.now(),
      ...(details && {
        userId: details.userId ?? currentInfo.userId,
        version: details.version ?? currentInfo.version,
        owner: details.owner ?? currentInfo.owner,
        isOwner: details.isOwner ?? currentInfo.isOwner,
        health: details.health ?? currentInfo.health,
        connectionId: details.connectionId ?? currentInfo.connectionId,
        errorDetails: details.errorDetails ?? (state === WebSocketConnectionStatus.ERROR ? details.errorDetails : null)
      })
    };

    this.connectionInfo$.next(newInfo);

    // Emit state change event if state actually changed
    if (previousState !== state) {
      this.stateChange$.next({
        from: previousState,
        to: state,
        timestamp: Date.now(),
        details
      });

      // console.log(`🔄 Connection state changed: ${previousState} → ${state}`);
    }
  }

  /**
   * Updates connection health
   */
  updateConnectionHealth(health: Partial<ConnectionHealth>): void {
    const currentInfo = this.connectionInfo$.getValue();
    const currentHealth = currentInfo.health || {
      healthy: false,
      lastPingTime: 0,
      lastPongTime: 0,
      lastMessageTime: 0,
      lastSendTime: 0,
      latency: 0,
      messagesReceived: 0,
      messagesSent: 0
    };

    const updatedHealth = { ...currentHealth, ...health };

    this.updateConnectionState(currentInfo.state, {
      health: updatedHealth
    });
  }

  /**
   * Updates connection statistics
   */
  updateStats(stats: ConnectionStats): void {
    this.stats$.next(stats);
  }

  /**
   * Emits a connection event
   */
  emitConnectionEvent(type: string, data: any): void {
    this.connectionEvents$.next({
      type,
      data,
      timestamp: Date.now()
    });
  }

  /**
   * Resets connection state to disconnected
   */
  resetConnection(): void {
    this.updateConnectionState(WebSocketConnectionStatus.DISCONNECTED, {
      userId: undefined,
      version: undefined,
      owner: undefined,
      isOwner: false,
      health: undefined,
      connectionId: undefined,
      errorDetails: undefined
    });
  }

  /**
   * Sets error state with details
   */
  setError(errorDetails: string): void {
    this.updateConnectionState(WebSocketConnectionStatus.ERROR, {
      errorDetails
    });
  }

  /**
   * Gets connection summary for debugging
   */
  getConnectionSummary(): {
    info: ConnectionInfo;
    stats: ConnectionStats | null;
    isHealthy: boolean;
    uptime: number;
  } {
    const info = this.connectionInfo$.getValue();
    const stats = this.stats$.getValue();
    
    return {
      info,
      stats,
      isHealthy: this.isHealthy(),
      uptime: stats?.uptime || 0
    };
  }

  /**
   * Observable that emits when connection becomes available
   */
  waitForConnection$(): Observable<ConnectionInfo> {
    return this.connectionInfo$.pipe(
      filter(info => info.state === WebSocketConnectionStatus.CONNECTED),
      distinctUntilChanged((prev, curr) => prev.version === curr.version)
    );
  }

  /**
   * Observable that emits when connection is lost
   */
  onConnectionLost$(): Observable<ConnectionInfo> {
    return this.connectionInfo$.pipe(
      filter(info => [
        WebSocketConnectionStatus.DISCONNECTED,
        WebSocketConnectionStatus.CLOSED,
        WebSocketConnectionStatus.ERROR
      ].includes(info.state))
    );
  }

  /**
   * Observable that emits when reconnecting
   */
  onReconnecting$(): Observable<ConnectionInfo> {
    return this.connectionInfo$.pipe(
      filter(info => info.state === WebSocketConnectionStatus.RECONNECTING)
    );
  }

  /**
   * Initializes state monitoring
   */
  private initializeStateMonitoring(): void {
    // Periodic state updates
    interval(this.config.stateUpdateInterval)
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.requestStateUpdate();
      });

    // Periodic stats updates
    interval(this.config.statsUpdateInterval)
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.requestStatsUpdate();
      });

    // Log significant state changes
    this.stateChange$
      .pipe(takeUntil(this.destroy$))
      .subscribe(change => {
        // console.log(`📊 Connection state transition:`, {
        //   from: change.from,
        //   to: change.to,
        //   timestamp: new Date(change.timestamp).toISOString(),
        //   details: change.details
        // });
      });

    // Monitor connection health
    this.connectionInfo$
      .pipe(
        takeUntil(this.destroy$),
        map(info => info.health),
        filter(health => health !== null),
        distinctUntilChanged((prev, curr) => prev?.healthy === curr?.healthy)
      )
      .subscribe(health => {
        if (health && !health.healthy) {
          // console.warn('⚠️ Connection health degraded');
          this.emitConnectionEvent('HEALTH_DEGRADED', health);
        } else if (health?.healthy) {
          // console.log('✅ Connection health restored');
          this.emitConnectionEvent('HEALTH_RESTORED', health);
        }
      });
  }

  /**
   * Requests state update from SharedWorker
   */
  private requestStateUpdate(): void {
    // This will be implemented when we integrate with WebSocketSharedService
    // For now, it's a placeholder
  }

  /**
   * Requests stats update from SharedWorker
   */
  private requestStatsUpdate(): void {
    // This will be implemented when we integrate with WebSocketSharedService
    // For now, it's a placeholder
  }

  /**
   * Processes incoming worker message
   */
  processWorkerMessage(message: any): void {
    const { type, timestamp } = message;

    switch (type) {
      case 'CONNECTED':
        this.updateConnectionState(WebSocketConnectionStatus.CONNECTED, {
          userId: message.userId,
          version: message.version,
          owner: message.owner,
          isOwner: message.isOwner,
          connectionId: message.connectionId
        });
        this.emitConnectionEvent('CONNECTED', message);
        break;

      case 'CONNECTING':
        this.updateConnectionState(WebSocketConnectionStatus.CONNECTING);
        this.emitConnectionEvent('CONNECTING', message);
        break;

      case 'DISCONNECTED':
        this.updateConnectionState(WebSocketConnectionStatus.DISCONNECTED, {
          errorDetails: message.reason
        });
        this.emitConnectionEvent('DISCONNECTED', message);
        break;

      case 'RECONNECTING':
        this.updateConnectionState(WebSocketConnectionStatus.RECONNECTING);
        this.emitConnectionEvent('RECONNECTING', message);
        break;

      case 'CONNECTION_ERROR':
        this.updateConnectionState(WebSocketConnectionStatus.ERROR, {
          errorDetails: message.error
        });
        this.emitConnectionEvent('CONNECTION_ERROR', message);
        break;

      case 'CONNECTION_FAILED':
        this.updateConnectionState(WebSocketConnectionStatus.ERROR, {
          errorDetails: message.error
        });
        this.emitConnectionEvent('CONNECTION_FAILED', message);
        break;

        case 'RECONNECT_FAILED':
            this.updateConnectionState(WebSocketConnectionStatus.ERROR, {
                errorDetails: message.reason || 'Max reconnect attempts reached'
            });
            this.emitConnectionEvent('RECONNECT_FAILED', message);
            break;

        case 'FORCE_RECONNECTING':
            this.updateConnectionState(WebSocketConnectionStatus.RECONNECTING);
            this.emitConnectionEvent('FORCE_RECONNECTING', message);
            break;

      case 'CONNECTION_BLOCKED':
        this.emitConnectionEvent('CONNECTION_BLOCKED', message);
        break;

      case 'OWNERSHIP_TRANSFERRED':
        const currentInfo = this.connectionInfo$.getValue();
        this.updateConnectionState(currentInfo.state, {
          isOwner: true
        });
        this.emitConnectionEvent('OWNERSHIP_TRANSFERRED', message);
        break;

      case 'CONNECTION_STATE':
        this.updateConnectionState(message.state as WebSocketConnectionStatus, {
          userId: message.userId,
          version: message.version,
          owner: message.owner,
          isOwner: message.isOwner,
          health: message.health
        });
        break;

      case 'STATS':
        this.updateStats(message.stats);
        break;

      case 'PONG':
        // Update health with pong response
        this.updateConnectionHealth({
          lastPongTime: timestamp,
          healthy: true
        });
        break;

      case 'MESSAGE_RECEIVED':
        // Update health with message activity
        this.updateConnectionHealth({
          lastMessageTime: timestamp,
          messagesReceived: (this.connectionInfo$.getValue().health?.messagesReceived || 0) + 1
        });
        break;

      case 'MESSAGE_SENT':
        // Update health with send activity
        this.updateConnectionHealth({
          lastSendTime: timestamp,
          messagesSent: (this.connectionInfo$.getValue().health?.messagesSent || 0) + 1
        });
        break;

      default:
        // Handle other message types as generic events
        this.emitConnectionEvent(type, message);
    }
  }

  /**
   * Gets user-friendly connection status
   */
  getConnectionStatus(): {
    status: string;
    description: string;
    color: 'success' | 'warning' | 'error' | 'info';
    icon: string;
  } {
    const info = this.connectionInfo$.getValue();
    const isHealthy = this.isHealthy();

    switch (info.state) {
      case WebSocketConnectionStatus.CONNECTED:
        return {
          status: isHealthy ? 'Connected' : 'Connected (Degraded)',
          description: isHealthy 
            ? 'Real-time messaging active' 
            : 'Connected but experiencing issues',
          color: isHealthy ? 'success' : 'warning',
          icon: isHealthy ? 'check-circle' : 'exclamation-triangle'
        };

      case WebSocketConnectionStatus.CONNECTING:
        return {
          status: 'Connecting...',
          description: 'Establishing connection',
          color: 'info',
          icon: 'clock'
        };

      case WebSocketConnectionStatus.RECONNECTING:
        return {
          status: 'Reconnecting...',
          description: 'Attempting to restore connection',
          color: 'warning',
          icon: 'refresh'
        };

      case WebSocketConnectionStatus.ERROR:
        return {
          status: 'Connection Error',
          description: info.errorDetails || 'Unable to connect',
          color: 'error',
          icon: 'exclamation-circle'
        };

      case WebSocketConnectionStatus.DISCONNECTED:
      case WebSocketConnectionStatus.CLOSED:
      default:
        return {
          status: 'Disconnected',
          description: 'No real-time connection',
          color: 'error',
          icon: 'x-circle'
        };
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    
    // Complete all subjects
    this.connectionInfo$.complete();
    this.stats$.complete();
    this.stateChange$.complete();
    this.connectionEvents$.complete();

    // console.log('🔗 ConnectionStateService destroyed');
  }
}